import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { ScrollText } from "lucide-react";

const ACTION_LABELS = {
  "login.success": "signed in",
  "login.failed": "failed sign-in",
  "login.deactivated": "deactivated account login attempt",
  "password.changed": "changed own password",
  "password.change_failed": "failed password change",
  "password.reset_requested": "requested password reset",
  "password.reset_completed": "completed password reset",
  "inventory.created": "created piece",
  "inventory.deleted": "deleted piece",
  "inventory.stage_changed": "moved piece stage",
  "inventory.marked_sold": "marked piece sold",
  "settings.updated": "updated settings",
  "user.created": "invited team member",
  "user.updated": "updated team member",
  "user.deleted": "removed team member",
  "user.password_reset": "reset team password",
  "expense.created": "logged expense",
  "expense.deleted": "removed expense",
};

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ action: "", entity_type: "", actor_email: "" });

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set("limit", "300");
    api.get(`/audit-logs?${params}`).then((r) => setLogs(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <AdminLayout>
      <div data-testid="admin-audit" className="max-w-6xl">
        <span className="editorial-label">trail · audit</span>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">every action, recorded.</h1>
        <p className="text-espresso max-w-2xl mb-10">a complete chronological log of what happened in the studio — who did what, when. masked secrets are shown as ***.</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 items-end">
          <input data-testid="audit-filter-action" placeholder="filter by action…" value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })} className="bg-transparent border-b border-taupe py-2 focus:outline-none focus:border-brass text-charcoal" />
          <select data-testid="audit-filter-entity" value={filter.entity_type} onChange={(e) => setFilter({ ...filter, entity_type: e.target.value })} className="bg-transparent border-b border-taupe py-2 focus:outline-none focus:border-brass text-charcoal">
            <option value="">all entities</option>
            <option value="user">user</option>
            <option value="inventory">inventory</option>
            <option value="settings">settings</option>
            <option value="expense">expense</option>
          </select>
          <input data-testid="audit-filter-actor" placeholder="filter by actor email…" value={filter.actor_email} onChange={(e) => setFilter({ ...filter, actor_email: e.target.value })} className="bg-transparent border-b border-taupe py-2 focus:outline-none focus:border-brass text-charcoal" />
          <button onClick={load} data-testid="audit-apply" className="bg-brass hover:bg-espresso text-ivory py-2 text-xs tracking-[0.25em] uppercase">apply filters</button>
        </div>

        <div className="overflow-x-auto border border-taupe/40">
          <table className="w-full text-sm">
            <thead className="bg-linen">
              <tr className="text-left">
                {["when", "actor", "action", "entity", "details"].map((h) => (
                  <th key={h} className="editorial-label px-5 py-4 border-b border-taupe/50">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id} className="border-b border-taupe/30 hover:bg-linen/50" data-testid={`audit-row-${i}`}>
                  <td className="px-5 py-3 text-xs text-taupe whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-5 py-3 text-charcoal">{l.actor_email || <span className="text-taupe">—</span>}</td>
                  <td className="px-5 py-3"><span className="text-xs tracking-widest uppercase text-brass">{ACTION_LABELS[l.action] || l.action}</span></td>
                  <td className="px-5 py-3 text-xs text-charcoal">{l.entity_type}{l.entity_id ? <span className="text-taupe"> · {l.entity_id.slice(0, 8)}</span> : null}</td>
                  <td className="px-5 py-3 text-xs text-espresso max-w-xs truncate" title={JSON.stringify(l.details)}>{l.details && Object.keys(l.details).length ? JSON.stringify(l.details) : "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan="5" className="text-center py-16">
                  <ScrollText className="w-8 h-8 text-taupe mx-auto mb-4" />
                  <p className="font-serif text-2xl lowercase text-taupe">no entries match.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
